import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { inputStyle, bigBtn, smallBtn, formOverlay, formContainer, card, fieldRow, fieldLabel, inlineInput, tableStyle, thStyle, trStyle } from '../lib/styles';

const emptyCrmDraft = { entity_name:"", city:"", state:"", entity_type:"SHIPPER", status:"NEW", contact_person:"", email:"", phone:"" };

interface CRMProps {
  data: any[];
  selectedLoad: any;
  setSelectedLoad: (v: any) => void;
  companyId: string | null;
  crmDraft: typeof emptyCrmDraft;
  setCrmDraft: (v: typeof emptyCrmDraft) => void;
  showEntryForm: boolean;
  setShowEntryForm: (v: boolean) => void;
  fetchRoomData: () => void;
  exportCrmCsv: () => void;
}

export default function CRM({ data, selectedLoad, setSelectedLoad, companyId, crmDraft, setCrmDraft, showEntryForm, setShowEntryForm, fetchRoomData, exportCrmCsv }: CRMProps) {
  const [editDraft, setEditDraft] = useState<any>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const openDrillDown = (item: any) => {
    setSelectedLoad(item);
    setEditDraft({ ...item });
    setEditSaved(false);
  };

  const saveEditDraft = async () => {
    if (!editDraft?.id) return;
    setEditSaving(true);
    const payload = { ...editDraft };
    delete payload.id; delete payload.company_id; delete payload.created_at;
    const { data: updated, error } = await (supabase as any).from('crm_contacts').update(payload).eq('id', editDraft.id).select('*').single();
    if (error) { alert(`Save failed: ${error.message}`); setEditSaving(false); return; }
    setSelectedLoad(updated);
    setEditDraft({ ...updated });
    fetchRoomData();
    setEditSaving(false);
    setEditSaved(true);
    setTimeout(() => setEditSaved(false), 2500);
  };

  const handleSave = async () => {
    if (!companyId) { alert("Company not loaded yet."); return; }
    const payload = { company_id:companyId, entity_name:crmDraft.entity_name.trim(), city:crmDraft.city.trim()||null, state:crmDraft.state.trim()||null, entity_type:crmDraft.entity_type, status:crmDraft.status, contact_person:crmDraft.contact_person.trim()||null, email:crmDraft.email.trim()||null, phone:crmDraft.phone.trim()||null };
    if (!payload.entity_name) { alert("Company name is required."); return; }
    const { error } = await supabase.from("crm_contacts").insert(payload);
    if (error) { alert(`CRM insert failed: ${error.message}`); return; }
    setShowEntryForm(false);
    setCrmDraft(emptyCrmDraft);
    fetchRoomData();
  };

  return (
    <>
      {/* ── CREATE ENTRY FORM ── */}
      {showEntryForm && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"580px", maxHeight:"90vh", overflowY:"auto" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>NEW CRM ENTRY</h3>
            <input type="text" placeholder="Company name *" style={inputStyle} value={crmDraft.entity_name} onChange={e=>setCrmDraft({...crmDraft,entity_name:e.target.value})} />
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="City" style={inputStyle} value={crmDraft.city} onChange={e=>setCrmDraft({...crmDraft,city:e.target.value})} />
              <input type="text" placeholder="State" style={inputStyle} value={crmDraft.state} onChange={e=>setCrmDraft({...crmDraft,state:e.target.value})} />
            </div>
            <select style={inputStyle as any} value={crmDraft.entity_type} onChange={e=>setCrmDraft({...crmDraft,entity_type:e.target.value})}>
              {["SHIPPER","BROKER","RECEIVER","CARRIER"].map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <input type="text" placeholder="Contact person" style={inputStyle} value={crmDraft.contact_person} onChange={e=>setCrmDraft({...crmDraft,contact_person:e.target.value})} />
            <input type="email" placeholder="Email" style={inputStyle} value={crmDraft.email} onChange={e=>setCrmDraft({...crmDraft,email:e.target.value})} />
            <input type="text" placeholder="Phone" style={inputStyle} value={crmDraft.phone} onChange={e=>setCrmDraft({...crmDraft,phone:e.target.value})} />
            <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
              <button onClick={handleSave} style={bigBtn}>SAVE TO SYSTEM</button>
              <button onClick={()=>setShowEntryForm(false)} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CRM LIST VIEW ── */}
      {!selectedLoad && (()=>{
        const fi: React.CSSProperties = { background:"#0a0a0a", border:"1px solid #2a2a2a", color:"#fff", padding:"6px 10px", fontFamily:"monospace", fontSize:"11px" };
        const q = search.toLowerCase();
        const rows = data.filter((c:any) => {
          if (q && !((c.entity_name||'').toLowerCase().includes(q) || (c.contact_person||'').toLowerCase().includes(q))) return false;
          if (typeFilter !== 'ALL' && (c.entity_type||'').toUpperCase() !== typeFilter) return false;
          if (statusFilter !== 'ALL' && (c.status||'').toUpperCase() !== statusFilter) return false;
          return true;
        });
        const typeColor = (t:string) => t==='SHIPPER'?'#4488FF':t==='BROKER'?'#FFD700':t==='RECEIVER'?'#aaa':t==='CARRIER'?'#32CD32':'#888';
        return (
        <div>
          <div style={{ display:"flex", gap:"10px", marginBottom:"16px", alignItems:"center", flexWrap:"wrap" }}>
            <input placeholder="Search company or contact..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...fi, width:"220px" }} />
            <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} style={fi as any}>
              {['ALL','SHIPPER','BROKER','RECEIVER','CARRIER','VENDOR','OTHER'].map(t=><option key={t} value={t}>{t==='ALL'?'ALL TYPES':t}</option>)}
            </select>
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={fi as any}>
              {['ALL','NEW','ACTIVE','PROSPECT','INACTIVE'].map(s=><option key={s} value={s}>{s==='ALL'?'ALL STATUSES':s}</option>)}
            </select>
            <button onClick={exportCrmCsv} style={{ marginLeft:"auto", background:"#0a0a0a", border:"1px solid #32CD32", color:"#32CD32", padding:"6px 12px", fontFamily:"monospace", fontSize:"11px", cursor:"pointer" }}>↓ EXPORT CSV</button>
            <span style={{ color:"#555", fontSize:"11px" }}>{rows.length} contact{rows.length!==1?'s':''}</span>
          </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>COMPANY</th>
              <th style={thStyle}>CITY</th>
              <th style={thStyle}>STATE</th>
              <th style={thStyle}>TYPE</th>
              <th style={thStyle}>CONTACT</th>
              <th style={thStyle}>STATUS</th>
              <th style={thStyle}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c:any)=>(
              <tr key={c.id} style={trStyle}>
                <td style={{ padding:"10px 12px", fontWeight:700 }}>{c.entity_name||"---"}</td>
                <td style={{ padding:"10px 12px", color:"#aaa" }}>{c.city||"---"}</td>
                <td style={{ padding:"10px 12px", color:"#aaa" }}>{c.state||"---"}</td>
                <td style={{ padding:"10px 12px" }}><span style={{ color:typeColor(c.entity_type||''), fontWeight:700, fontSize:"10px" }}>{c.entity_type||"---"}</span></td>
                <td style={{ padding:"10px 12px", color:"#aaa" }}>{c.contact_person||"---"}</td>
                <td style={{ padding:"10px 12px", color:c.status==='ACTIVE'?'#32CD32':c.status==='INACTIVE'?'#555':'#aaa' }}>{c.status||"NEW"}</td>
                <td style={{ padding:"10px 12px" }}><button onClick={()=>openDrillDown(c)} style={smallBtn}>OPEN</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        );
      })()}

      {/* ── CRM DETAIL / DRILL-DOWN ── */}
      {selectedLoad && (
        <div style={card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
            <button onClick={()=>setSelectedLoad(null)} style={{ color:"#32CD32", background:"none", border:"none", cursor:"pointer", fontSize:"13px" }}>{"← BACK TO CRM"}</button>
            <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
              {editSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
              <button onClick={saveEditDraft} disabled={editSaving} style={{ ...smallBtn, background:editSaving?"#1a1a1a":"#32CD32", color:editSaving?"#555":"#000" }}>{editSaving?"SAVING...":"SAVE CHANGES"}</button>
            </div>
          </div>
          <h3 style={{ marginTop:0 }}>COMPANY: <input style={{ ...inlineInput, fontSize:"16px", fontWeight:700, width:"280px" }} value={editDraft?.entity_name||""} onChange={e=>setEditDraft({...editDraft,entity_name:e.target.value})} /></h3>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" }}>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>COMPANY INFO</div>
              <div style={fieldRow}><span style={fieldLabel}>TYPE</span>
                <select style={inlineInput as any} value={editDraft?.entity_type||"BROKER"} onChange={e=>setEditDraft({...editDraft,entity_type:e.target.value})}>
                  {["BROKER","SHIPPER","RECEIVER","CARRIER","VENDOR","OTHER"].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>STATUS</span>
                <select style={inlineInput as any} value={editDraft?.status||"NEW"} onChange={e=>setEditDraft({...editDraft,status:e.target.value})}>
                  {["NEW","ACTIVE","PROSPECT","INACTIVE","DO NOT USE"].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>CITY</span><input style={inlineInput} value={editDraft?.city||""} onChange={e=>setEditDraft({...editDraft,city:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>STATE</span><input style={inlineInput} value={editDraft?.state||""} onChange={e=>setEditDraft({...editDraft,state:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>ADDRESS</span><input style={inlineInput} value={editDraft?.address1||""} onChange={e=>setEditDraft({...editDraft,address1:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>MC / DOT #</span><input style={inlineInput} value={editDraft?.mc_number||editDraft?.dot_number||""} onChange={e=>setEditDraft({...editDraft,mc_number:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>CREDIT LIMIT ($)</span><input type="number" style={inlineInput} value={editDraft?.credit_limit||""} onChange={e=>setEditDraft({...editDraft,credit_limit:Number(e.target.value)})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>PAYMENT TERMS</span><input style={inlineInput} value={editDraft?.payment_terms||""} onChange={e=>setEditDraft({...editDraft,payment_terms:e.target.value})} placeholder="e.g. Net 30, Quick Pay" /></div>
            </div>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>CONTACT</div>
              <div style={fieldRow}><span style={fieldLabel}>PERSON</span><input style={inlineInput} value={editDraft?.contact_person||""} onChange={e=>setEditDraft({...editDraft,contact_person:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>EMAIL</span><input style={inlineInput} value={editDraft?.email||""} onChange={e=>setEditDraft({...editDraft,email:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>PHONE</span><input style={inlineInput} value={editDraft?.phone||""} onChange={e=>setEditDraft({...editDraft,phone:e.target.value})} /></div>
              <div style={{ marginTop:"12px" }}>
                <div style={{ color:"#555", fontSize:"10px", marginBottom:"6px" }}>NOTES</div>
                <textarea style={{ ...inlineInput, resize:"vertical", minHeight:"100px" } as any} value={editDraft?.notes||""} onChange={e=>setEditDraft({...editDraft,notes:e.target.value})} placeholder="Broker notes, payment history, contacts, preferences..." />
              </div>
            </div>
          </div>
          {(() => {
            const crmLoads = data.filter((l:any)=>l.customer_name?.toLowerCase()===selectedLoad.entity_name?.toLowerCase())||[];
            if (crmLoads.length===0) return null;
            return (
              <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0a0a0a" }}>
                <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"12px" }}>LOAD HISTORY ({crmLoads.length})</div>
                <table style={tableStyle}>
                  <thead><tr><th style={thStyle}>LOAD #</th><th style={thStyle}>STATUS</th><th style={thStyle}>ORIGIN</th><th style={thStyle}>DESTINATION</th><th style={thStyle}>RATE</th></tr></thead>
                  <tbody>
                    {crmLoads.map((l:any,i:number)=>(
                      <tr key={i} style={trStyle}>
                        <td style={{ padding:"8px 12px", color:"#32CD32" }}>{l.load_number}</td>
                        <td style={{ padding:"8px 12px" }}>{l.status}</td>
                        <td style={{ padding:"8px 12px" }}>{l.pickup_city},{l.pickup_state}</td>
                        <td style={{ padding:"8px 12px" }}>{l.delivery_city},{l.delivery_state}</td>
                        <td style={{ padding:"8px 12px", color:"#32CD32" }}>${Number(l.rate_total||0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
}
